import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  Alert,
  Breadcrumb,
  Button,
  Empty,
  Input,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  ExportOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {makeSelectSubTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {GenerateContentModal} from './GenerateContentModal';
import {TransformationDetailsModal} from './TransformationDetailsModal';
import {useContentTransformations} from './useContentTransformations';
import {MAX_SEARCH_LENGTH, PAGE_SIZE_OPTIONS} from './constants';
import {
  UNTITLED,
  channelLabel,
  errorCodeOf,
  errorMessageOf,
  formatDateTime,
  languageLabel,
  statusMeta,
  truncate,
} from './utils';
import styles from './ContentTransformations.module.scss';

const FLOW_BUILDER_SUBTAB = 'flow-builder';

const ChannelChips = ({channels = []}) => {
  const shown = channels.slice(0, 3);
  const rest = channels.length - shown.length;
  return (
    <Space size={[4, 4]} wrap>
      {shown.map(channel => (
        <Tag key={channel}>{channelLabel(channel)}</Tag>
      ))}
      {rest > 0 ? (
        <Tooltip title={channels.slice(3).map(channelLabel).join(', ')}>
          <Tag>+{rest} more</Tag>
        </Tooltip>
      ) : null}
    </Space>
  );
};

export const ContentTransformations = () => {
  const navigate = useNavigate();
  const operations = useSelector(state =>
    makeSelectSubTabOperations(
      state,
      NavTabsEnum.AiAutomationTab,
      FLOW_BUILDER_SUBTAB,
    ),
  );

  const canView = Boolean(operations?.view);
  const canCreate = Boolean(operations?.create);

  // Skip the history query entirely for admins without VIEW so the page never
  // fires a request it is not allowed to make.
  const ct = useContentTransformations({enabled: canView});

  if (!canView) {
    return (
      <div className={styles.root}>
        <Alert
          type="error"
          showIcon
          message="Access denied"
          description="You do not have permission to view content transformations."
        />
      </div>
    );
  }

  const columns = [
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: formatDateTime,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (value, record) => (
        <Tooltip title={value || record.content}>
          <span className={styles.titleCell}>{value || UNTITLED}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      width: 110,
      render: languageLabel,
    },
    {
      title: 'Channels',
      dataIndex: 'channels',
      key: 'channels',
      render: channels => <ChannelChips channels={channels} />,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (value, record) => {
        const meta = statusMeta(value);
        const tag = <Tag color={meta.color}>{meta.label}</Tag>;
        return record.error ? (
          <Tooltip title={truncate(record.error, 160)}>{tag}</Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: 'Execution',
      key: 'execution',
      width: 160,
      render: (_, record) =>
        record.executionUrl ? (
          <a
            href={record.executionUrl}
            target="_blank"
            rel="noopener noreferrer">
            Open execution <ExportOutlined />
          </a>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => ct.openDetails(record)}>
          View details
        </Button>
      ),
    },
  ];

  const historyErrorMessage = ct.listError
    ? errorCodeOf(ct.listError) === 'CONTENT_TRANSFORMATION_HISTORY_ERROR'
      ? errorMessageOf(ct.listError) ||
        'Content transformation history could not be loaded.'
      : errorMessageOf(ct.listError)
    : null;

  const total = ct.pagination?.total ?? 0;

  const emptyState = ct.isSearching ? (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div className={styles.emptyBlock}>
          <strong>No matching transformations</strong>
          <span>Try another title, request ID, status, or content search.</span>
        </div>
      }>
      <Button onClick={ct.clearSearch}>Clear search</Button>
    </Empty>
  ) : (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div className={styles.emptyBlock}>
          <strong>No content transformations yet</strong>
          <span>
            Generate content for one or more channels and the workflow runs will
            appear here.
          </span>
        </div>
      }>
      {canCreate ? (
        <Button type="primary" icon={<PlusOutlined />} onClick={ct.openCreate}>
          Generate content
        </Button>
      ) : null}
    </Empty>
  );

  return (
    <div className={styles.root}>
      <Breadcrumb className={styles.breadcrumb}>
        <Breadcrumb.Item>
          <span
            className={styles.crumbLink}
            role="button"
            tabIndex={0}
            onClick={() => navigate('/dashboard/ai-automation')}
            onKeyDown={e => {
              if (e.key === 'Enter') navigate('/dashboard/ai-automation');
            }}>
            AI Automation
          </span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span
            className={styles.crumbLink}
            role="button"
            tabIndex={0}
            onClick={() => navigate('/dashboard/ai-automation/flow-builder')}
            onKeyDown={e => {
              if (e.key === 'Enter')
                navigate('/dashboard/ai-automation/flow-builder');
            }}>
            Flow Builder
          </span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Content transformations</Breadcrumb.Item>
      </Breadcrumb>

      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h2>Content transformations</h2>
          <p>
            Generate content for supported output channels and monitor workflow
            progress.
          </p>
        </div>
        <Space>
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={ct.refetchHistory}
              loading={ct.refreshing}>
              Refresh
            </Button>
          </Tooltip>
          {canCreate ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={ct.openCreate}>
              Generate content
            </Button>
          ) : null}
        </Space>
      </div>

      <div className={styles.toolbar}>
        <Input
          allowClear
          className={styles.searchInput}
          prefix={<SearchOutlined />}
          maxLength={MAX_SEARCH_LENGTH}
          placeholder="Search by title, content, request ID or status"
          value={ct.search}
          onChange={e => ct.setSearch(e.target.value)}
        />
        <span className={styles.muted}>
          {total} {total === 1 ? 'record' : 'records'}
        </span>
      </div>

      {historyErrorMessage ? (
        <Alert
          type="error"
          showIcon
          className={styles.errorBanner}
          message={historyErrorMessage}
          action={
            <Button size="small" onClick={ct.refetchHistory}>
              Try again
            </Button>
          }
        />
      ) : null}

      <Table
        className="antd-table"
        rowKey="id"
        columns={columns}
        dataSource={ct.rows}
        // Polling refetches every 10s while runs are active — only show the
        // spinner on the first load so the table does not flash.
        loading={ct.listLoading && !ct.rows.length}
        scroll={{x: 1100}}
        locale={{emptyText: ct.listLoading ? ' ' : emptyState}}
        pagination={
          total
            ? {
                current: ct.pagination?.page || ct.page,
                pageSize: ct.pagination?.limit || ct.limit,
                total,
                showSizeChanger: true,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onChange: ct.handlePageChange,
                onShowSizeChange: ct.handlePageChange,
              }
            : false
        }
      />

      <GenerateContentModal
        open={ct.createOpen}
        form={ct.form}
        submitting={ct.submitting}
        formError={ct.formError}
        onClose={ct.closeCreate}
        onSubmit={ct.submit}
      />

      <TransformationDetailsModal
        open={ct.detailsOpen}
        record={ct.detailsRecord}
        loading={ct.detailsLoading}
        onClose={ct.closeDetails}
      />
    </div>
  );
};
